// src/features/agent-builder/components/PlatformToolItem.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleSwitchField, Tooltip } from '@dopomogai/ui'; // Added Tooltip
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';
import type { PlatformToolData } from '@dopomogai/types';
import { Lock } from 'lucide-react'; // For the lock icon

interface PlatformToolItemProps {
  tool: PlatformToolData;
  onToggle: (toolId: string, isEnabled: boolean) => void;
  // No edit/delete for platform tools in this component
}

export const PlatformToolItem: React.FC<PlatformToolItemProps> = ({ tool, onToggle }) => {
  const { t } = useTranslation(['agentBuilder', 'common']);

  const toolName = t(tool.nameKey, { ns: 'agentBuilder', defaultValue: tool.nameKey });
  const toolDescription = t(tool.descriptionKey, { ns: 'agentBuilder', defaultValue: tool.descriptionKey });
  const isActuallyMutable = tool.isMutable !== false; // Defaults to true if undefined

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isActuallyMutable) {
      onToggle(tool.id, event.target.checked);
    }
  };

  const toggleLabelText = isActuallyMutable
    ? (tool.isEnabled ? t('common:enabled') : t('common:disabled'))
    : t('common:locked');

  return (
    <div className={clsx(
      "flex items-center justify-between p-4 bg-white rounded-xl",
      "border-2 border-black",
      "transition-colors duration-150 ease-in-out",
      isActuallyMutable && "hover:bg-slate-50",
      !isActuallyMutable && "opacity-70" // No cursor-default, let ToggleSwitchField handle its disabled cursor
    )}>
      {/* Left Section: Icon and Info */}
      <div className="flex items-start gap-4 flex-grow min-w-0 pr-4">
        <div className="flex-shrink-0 pt-1 text-slate-600">
          {renderIcon(tool.icon || 'settings', { size: 28, className: "opacity-80" })}
        </div>
        <div className="flex-grow min-w-0">
          <h3 className="text-md font-semibold text-slate-800 truncate" title={toolName}>
            {toolName}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed line-clamp-2" title={toolDescription}>
            {toolDescription}
          </p>
        </div>
      </div>

      {/* Right Section: Toggle */}
      <div className="ml-auto flex items-center gap-2 flex-shrink-0 pl-2">
        {!isActuallyMutable && (
          <Tooltip text={t('common:lockedTooltip', 'This tool is locked and cannot be disabled.')}>
            <Lock size={18} className="text-slate-500" />
          </Tooltip>
        )}
        <ToggleSwitchField
          id={`platform-tool-toggle-${tool.id}`}
          checked={!!tool.isEnabled}
          onChange={handleToggleChange}
          disabled={!isActuallyMutable}
          aria-label={isActuallyMutable ? `${toolName}: ${toggleLabelText}` : `${toolName}: ${t('common:locked')}`}
          // The ToggleSwitchField component from @dopomogai/ui should handle its own Neobrutalist styling,
          // including visual feedback for enabled/disabled states and focus.
          // If you want visible text like "Enabled/Disabled", and your ToggleSwitchField
          // doesn't directly render it via a `label` prop + `labelPosition`, you'd add a separate span here.
        />
      </div>
    </div>
  );
};